import { useCallback, useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import type { MessageKey } from '@lensmira/i18n'

import { useTranslation } from '../i18n.js'

/**
 * Finding the customer a prescription belongs to. §6.2 `CUS-003`, Appendix K, `KB-02`.
 *
 * A read, so it does not go through the write queue: `OFF-001a` is about writes, and `OFF-001b`
 * says reads are served from a cached working set, which is a later slice. Until then this asks the
 * API directly through the one fetch this screen is allowed, and a failed search degrades to an
 * empty list rather than to an error, because a counter that cannot search still has to be able to
 * type a prescription.
 *
 * Keyboard first throughout (`KB-02`): the arrow keys walk the results and Enter picks one, so a
 * dispenser never has to reach for a mouse between finding the customer and typing the numbers.
 */

export interface CustomerMatch {
  readonly id: string
  readonly name: string
  readonly matchedOn: 'name' | 'sound' | 'phone'
}

export interface CustomerSearchProps {
  readonly baseUrl: string
  readonly token: string
  readonly onPick: (customer: CustomerMatch) => void
  readonly doFetch: typeof globalThis.fetch
}

/** Appendix K's three signals, said in words a shopkeeper reads rather than in field names. */
const WHY: Readonly<Record<CustomerMatch['matchedOn'], MessageKey>> = {
  name: 'customerSearch.why.name',
  sound: 'customerSearch.why.sound',
  phone: 'customerSearch.why.phone',
}

export function CustomerSearch({
  baseUrl,
  token,
  onPick,
  doFetch,
}: CustomerSearchProps): JSX.Element {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [matches, setMatches] = useState<readonly CustomerMatch[]>([])
  const [highlighted, setHighlighted] = useState(0)
  const [searching, setSearching] = useState(false)
  const field = useRef<HTMLInputElement>(null)

  useEffect(() => {
    field.current?.focus()
  }, [])

  useEffect(() => {
    if (query.trim().length < 2) {
      setMatches([])
      return
    }
    let cancelled = false
    // Debounced, because this fires on every keystroke and a Pakistani shop's uplink is the
    // constraint the whole product is designed around.
    const timer = setTimeout(() => {
      setSearching(true)
      void doFetch(`${baseUrl}/customers?q=${encodeURIComponent(query)}`, {
        headers: { authorization: `Bearer ${token}` },
      })
        .then(async (r) => (r.ok ? ((await r.json()) as { matches: CustomerMatch[] }) : null))
        .then((body) => {
          if (cancelled) return
          setMatches(body?.matches ?? [])
          setHighlighted(0)
        })
        .catch(() => {
          // Offline. An empty list, not an error: the dispenser can still type the prescription.
          if (!cancelled) setMatches([])
        })
        .finally(() => {
          if (!cancelled) setSearching(false)
        })
    }, 250)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [baseUrl, doFetch, query, token])

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (matches.length === 0) return
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setHighlighted((h) => Math.min(h + 1, matches.length - 1))
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        setHighlighted((h) => Math.max(h - 1, 0))
      } else if (event.key === 'Enter') {
        event.preventDefault()
        const picked = matches[highlighted]
        if (picked) onPick(picked)
      }
    },
    [highlighted, matches, onPick],
  )

  return (
    <section className="lm-screen lm-screen--narrow" aria-labelledby="customer-search-heading">
      <h1 id="customer-search-heading" className="lm-screen__heading">
        {t('customerSearch.heading')}
      </h1>

      <label className="lm-field">
        <span className="lm-field__label">{t('customerSearch.label')}</span>
        <input
          ref={field}
          className="lm-input"
          // `LOC-005`: this field takes a name in Urdu or Latin script or a phone number, so it
          // follows what is typed rather than the page. A phone number starts with a digit, which
          // is direction-neutral, and then reads as the page does; the match list below is where
          // the name is shown whole.
          dir="auto"
          value={query}
          autoComplete="off"
          role="combobox"
          aria-expanded={matches.length > 0}
          aria-controls="customer-matches"
          onChange={(e) => setQuery(e.currentTarget.value)}
          onKeyDown={onKeyDown}
        />
      </label>

      <ul id="customer-matches" className="lm-matches" role="listbox">
        {matches.map((match, position) => (
          <li key={match.id}>
            <button
              type="button"
              role="option"
              aria-selected={position === highlighted}
              className={position === highlighted ? 'lm-match lm-match--highlighted' : 'lm-match'}
              onClick={() => onPick(match)}
            >
              {/* `LOC-005` `MUST`: "Names, addresses and phone numbers must not assume Western formats."
                  So a customer's name takes its own direction. An Urdu name inside an
                  English page, or a Latin name inside an Urdu one, must not be reordered. */}
              <span className="lm-match__name" dir="auto">
                {match.name}
              </span>
              {/* CL-02's habit applied to a search result: say why the row is here in words, not
                  only by ordering it higher. A phonetic match on an Urdu name typed in Latin looks
                  like a wrong answer until the reason is on screen. */}
              <span className="lm-match__why">{t(WHY[match.matchedOn])}</span>
            </button>
          </li>
        ))}
      </ul>

      <p className="lm-hint" role="status">
        {query.trim().length < 2
          ? t('customerSearch.hint.short')
          : searching
            ? t('customerSearch.hint.searching')
            : matches.length === 0
              ? t('customerSearch.hint.none')
              : t('customerSearch.hint.found', { count: matches.length })}
      </p>
    </section>
  )
}
